/**
 * FaceTracker - wraps MediaPipe FaceMesh (proven stable head-pose tracking).
 * Exposes normalized head-pose offset (x,y,z):
 *   x: -0.5 (turned left)  .. +0.5 (turned right)
 *   y: -0.5 (looking down) .. +0.5 (looking up)
 *   z: -0.5 (far)          .. +0.5 (close)
 */

const MIRROR = true;

/**
 * OneEuroFilter — Adaptive low-pass filter (Casiez et al., ACM CHI 2012).
 * Eliminates high-frequency webcam sensor jitter when stationary (low cutoff frequency)
 * while eliminating latency/lag during movement (cutoff frequency scales dynamically with speed).
 */
class OneEuroFilter {
  constructor({ minCutoff = 0.35, beta = 1.8, dCutoff = 1.0 } = {}) {
    this.minCutoff = minCutoff; // lower = rock-solid at rest (Hz)
    this.beta = beta;           // higher = faster response / zero lag during motion
    this.dCutoff = dCutoff;     // cutoff for derivative estimation (Hz)
    this.xPrev = null;
    this.dxPrev = 0;
    this.tPrev = null;
  }

  alpha(cutoff, dt) {
    const tau = 1.0 / (2 * Math.PI * cutoff);
    return 1.0 / (1.0 + tau / dt);
  }

  filter(x, t) {
    if (this.xPrev === null) {
      this.xPrev = x;
      this.tPrev = t;
      return x;
    }
    const dt = Math.max((t - this.tPrev) / 1000, 1e-4);
    this.tPrev = t;

    // Filter derivative (velocity)
    const dx = (x - this.xPrev) / dt;
    const aD = this.alpha(this.dCutoff, dt);
    const edx = this.dxPrev + aD * (dx - this.dxPrev);
    this.dxPrev = edx;

    // Adaptive cutoff based on speed
    const cutoff = this.minCutoff + this.beta * Math.abs(edx);
    const a = this.alpha(cutoff, dt);
    const filtered = this.xPrev + a * (x - this.xPrev);
    this.xPrev = filtered;
    return filtered;
  }

  reset() {
    this.xPrev = null;
    this.dxPrev = 0;
    this.tPrev = null;
  }
}

export class FaceTracker {
  constructor({ videoEl, onUpdate, onStatus }) {
    this.videoEl = videoEl;
    this.onUpdate = onUpdate;
    this.onStatus = onStatus || (() => {});
    this.faceMesh = null;
    this.stream = null;
    this.running = false;
    this.smoothed = { x: 0, y: 0, z: 0 };
    this.hasFace = false;
    this._raf = null;
    this._processing = false;

    // Adaptive 1 Euro Filters for x (yaw), y (pitch), z (distance)
    this.filterX = new OneEuroFilter({ minCutoff: 0.35, beta: 1.8, dCutoff: 1.0 });
    this.filterY = new OneEuroFilter({ minCutoff: 0.35, beta: 1.8, dCutoff: 1.0 });
    this.filterZ = new OneEuroFilter({ minCutoff: 0.22, beta: 1.2, dCutoff: 1.0 });

    // Adaptive baseline for eye distance (neutral resting position)
    this.baseEyeDist = 0.095;
    this.calibFrames = 0;
  }

  async start() {
    if (!window.FaceMesh) throw new Error("MediaPipe FaceMesh not loaded");
    this.onStatus("requesting camera");

    this.stream = await navigator.mediaDevices.getUserMedia({
      video: { width: 640, height: 480, facingMode: "user" },
      audio: false,
    });
    this.videoEl.srcObject = this.stream;
    await this.videoEl.play();

    this.onStatus("loading face model");
    const publicUrl = process.env.PUBLIC_URL || "";
    this.faceMesh = new window.FaceMesh({
      locateFile: (file) => `${publicUrl}/models/face_mesh/${file}`,
    });
    this.faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: false,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });
    this.faceMesh.onResults((results) => this._onResults(results));
    await this.faceMesh.initialize?.();

    this.running = true;
    this.onStatus("tracking");
    this._loop();
  }

  _loop = async () => {
    if (!this.running) return;
    if (!this._processing && this.videoEl.readyState >= 2) {
      this._processing = true;
      try {
        await this.faceMesh.send({ image: this.videoEl });
      } catch (e) { /* transient */ }
      this._processing = false;
    }
    this._raf = requestAnimationFrame(this._loop);
  };

  _onResults(results) {
    const lm = results.multiFaceLandmarks && results.multiFaceLandmarks[0];
    if (!lm) {
      this.hasFace = false;
      this.filterX.reset();
      this.filterY.reset();
      this.filterZ.reset();
      this.smoothed.x *= 0.94;
      this.smoothed.y *= 0.94;
      this.smoothed.z *= 0.94;
      this.onUpdate({ ...this.smoothed, hasFace: false });
      return;
    }
    if (!this.hasFace) {
      // Re-acquired face: allow gentle recalibration to current resting distance
      this.calibFrames = Math.min(this.calibFrames, 15);
    }
    this.hasFace = true;

    const nose = lm[1];
    const leftEye = lm[33];
    const rightEye = lm[263];

    const dx = rightEye.x - leftEye.x;
    const dy = rightEye.y - leftEye.y;
    const eyeDist = Math.sqrt(dx * dx + dy * dy);

    // Dynamic adaptive baseline for neutral resting distance
    if (this.calibFrames < 35) {
      if (this.calibFrames === 0) {
        this.baseEyeDist = eyeDist;
      } else {
        this.baseEyeDist += (eyeDist - this.baseEyeDist) * 0.12;
      }
      this.calibFrames++;
    } else {
      // Very slow drift to adapt to natural posture shifts over minutes (tau ~ 60s)
      this.baseEyeDist += (eyeDist - this.baseEyeDist) * 0.0002;
    }
    this.baseEyeDist = Math.max(0.05, Math.min(0.18, this.baseEyeDist));

    const midEyeX = (leftEye.x + rightEye.x) / 2;
    const midEyeY = (leftEye.y + rightEye.y) / 2;

    // yaw: horizontal offset of nose from eye midpoint, normalized by eye-dist
    let yawRaw = (nose.x - midEyeX) / (eyeDist + 1e-6);
    if (MIRROR) yawRaw = -yawRaw;

    // pitch: vertical nose-eye distance vs baseline (~0.62)
    const pitchRaw = -(((nose.y - midEyeY) / (eyeDist + 1e-6)) - 0.62);

    // Scale-invariant depth: ratio of current eye distance relative to calibrated neutral baseline
    const relZ = (eyeDist - this.baseEyeDist) / (this.baseEyeDist + 1e-6);
    const DZ_Z = 0.02; // 2% deadzone to eliminate micro-jitter at rest
    let zSignal = 0;
    if (Math.abs(relZ) > DZ_Z) {
      const sign = relZ > 0 ? 1 : -1;
      zSignal = sign * ((Math.abs(relZ) - DZ_Z) / (1 - DZ_Z));
    }
    let zRaw = Math.max(-0.7, Math.min(1.0, zSignal * 2.8));

    let yaw = Math.max(-0.7, Math.min(0.7, yawRaw));
    let pitch = Math.max(-0.6, Math.min(0.6, pitchRaw));

    // Deadzone for neutral forward gaze to prevent micro-drift at rest
    const DZ = 0.03;
    const softDead = (v) => {
      const a = Math.abs(v);
      if (a < DZ) return 0;
      const sign = v < 0 ? -1 : 1;
      return sign * ((a - DZ) / (1 - DZ));
    };
    yaw = softDead(yaw);
    pitch = softDead(pitch);

    // Filter through OneEuroFilter (adaptive: rock-solid when still, zero lag when moving)
    const now = performance.now();
    this.smoothed.x = this.filterX.filter(yaw, now);
    this.smoothed.y = this.filterY.filter(pitch, now);
    this.smoothed.z = this.filterZ.filter(zRaw, now);

    this.onUpdate({ ...this.smoothed, hasFace: true });
  }

  stop() {
    this.running = false;
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this.faceMesh) this.faceMesh.close?.();
    if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
    this.filterX.reset();
    this.filterY.reset();
    this.filterZ.reset();
    this.calibFrames = 0;
    this.baseEyeDist = 0.095;
  }
}
